import { useState } from 'react';
import { FORMATION_SHAPES } from '../../domain/setup';
import type { TeamShape } from '../../types';
import { SheetB } from '../SheetB';

/**
 * §17.3 — the **team-shape sheet**: three fields, one Save, **one `UpdateSetting`, one
 * transaction** (build unit B-28).
 *
 * ── One key, not three, and it is a correctness argument ────────────────────
 *
 * The bus opens exactly one transaction per command, so three keys would be three dispatches with
 * no atomicity across them — and an iOS kill between two of them would leave a profile whose
 * in-possession shape saved and whose out-of-possession shape did not. §3.7.38 states this is
 * *one* fact (*"two shapes and the mechanism between them"*) and §3.7.39 requires them *"presented
 * as phases of one team rather than alternatives"*. One key = one command = one transaction = the
 * whole shape commits or none of it does.
 *
 * ── Free text, not a formation enum ────────────────────────────────────────
 *
 * `FORMATION_SHAPES` describes the **opponent's** guessed formation on **one match**, from a fixed
 * list. Andrew's in-possession shape is *"more like a 2-4-2 or a 2-3-1-2"* — a range. Forcing that
 * into an enum member discards the thing he actually said. So the values are offered as tap-chips
 * (a pure read of an existing domain constant, never the storage type) and **free text is always
 * accepted**.
 *
 * ── The transition placeholder is doing real work ──────────────────────────
 *
 * *"the left back pushes into midfield if there's no one waiting high to run in behind"* is the
 * stakeholder's own sentence, and it teaches that **conditions** belong in that field — which is
 * §3.7.38's entire point and the one thing a formation notation cannot express.
 */

/**
 * The chips the two shape fields offer.
 *
 * The **9v9** list, because that is the format the stakeholder's team plays and this is a fact
 * about *his* team rather than about one match — which is exactly why the shape is not stored as a
 * `MatchFormat`-scoped enum in the first place. They are a shortcut for typing, never a
 * constraint: anything typed is kept verbatim.
 */
const SHAPE_CHIPS: readonly string[] = FORMATION_SHAPES['9v9'].filter(
  (shape) => /^\d/.test(shape),
);

export interface ShapeSheetProps {
  readonly shape: TeamShape;
  readonly busy: boolean;
  readonly problem: string | null;
  readonly onSave: (shape: Omit<TeamShape, 'updatedAt'>) => void;
  readonly onClose: () => void;
}

export function ShapeSheet({ shape, busy, problem, onSave, onClose }: ShapeSheetProps) {
  const [outOfPossession, setOutOfPossession] = useState(shape.outOfPossession);
  const [inPossession, setInPossession] = useState(shape.inPossession);
  const [transition, setTransition] = useState(shape.transition);

  return (
    <SheetB
      titleId="myteam-shape-title"
      title="Team shape"
      testId="myteam-shape-sheet"
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="sheet-b__primary"
            data-testid="myteam-shape-save"
            disabled={busy}
            onClick={() => {
              onSave({ outOfPossession, inPossession, transition });
            }}
          >
            Save
          </button>
          <button
            type="button"
            className="sheet-b__secondary"
            data-testid="myteam-shape-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
        </>
      }
    >
      <label className="sheet-b__label" htmlFor="myteam-shape-out">
        Out of possession we set up in…
      </label>
      <input
        id="myteam-shape-out"
        className="sheet-b__field"
        data-testid="myteam-shape-out"
        type="text"
        autoComplete="off"
        placeholder="3-3-2"
        value={outOfPossession}
        onChange={(event) => {
          setOutOfPossession(event.target.value);
        }}
      />
      <ShapeChips testId="myteam-shape-out-chip" disabled={busy} onPick={setOutOfPossession} />

      <label className="sheet-b__label" htmlFor="myteam-shape-in">
        In possession we become…
      </label>
      <input
        id="myteam-shape-in"
        className="sheet-b__field"
        data-testid="myteam-shape-in"
        type="text"
        autoComplete="off"
        placeholder="3-2-3, or “more like a 2-4-2”"
        value={inPossession}
        onChange={(event) => {
          setInPossession(event.target.value);
        }}
      />
      <ShapeChips testId="myteam-shape-in-chip" disabled={busy} onPick={setInPossession} />

      <label className="sheet-b__label" htmlFor="myteam-shape-transition">
        How you move between them
      </label>
      <textarea
        id="myteam-shape-transition"
        className="sheet-b__field sheet-b__field--area"
        data-testid="myteam-shape-transition"
        rows={6}
        placeholder="the left back pushes into midfield if there’s no one waiting high to run in behind"
        value={transition}
        onChange={(event) => {
          setTransition(event.target.value);
        }}
      />
      <p className="sheet-b__hint" data-testid="myteam-shape-hint">
        Conditions belong here — when it happens, and when it doesn’t. That is the part no
        formation can say.
      </p>

      {problem !== null && (
        <p className="sheet-b__problem" data-testid="myteam-shape-problem" role="status">
          {problem}
        </p>
      )}
    </SheetB>
  );
}

function ShapeChips({
  testId,
  disabled,
  onPick,
}: {
  readonly testId: string;
  readonly disabled: boolean;
  readonly onPick: (shape: string) => void;
}) {
  return (
    <div className="myteam-chips" role="group" aria-label="Common shapes">
      {SHAPE_CHIPS.map((shape) => (
        <button
          key={shape}
          type="button"
          className="myteam-chip myteam-chip--shape"
          data-testid={`${testId}-${shape}`}
          disabled={disabled}
          onClick={() => {
            onPick(shape);
          }}
        >
          {shape}
        </button>
      ))}
    </div>
  );
}
